import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MapPin, Heart, Camera, Award } from "lucide-react";
import Logo from "../components/Logo";
import { openWhatsApp } from "../lib/whatsapp";

/**
 * Reusable SEO landing page for a specific city.
 * Each city has unique copy, H1, intro and local references.
 * Updates document.title and meta description on mount so the page works
 * properly as a single-page-app route (no SSR needed).
 */
export default function LocationPage({ city, data }) {
    useEffect(() => {
        const title = `Fotógrafa de bodas en ${city} | Silvia García Fotografía`;
        document.title = title;
        const desc = data.metaDescription;
        const setMeta = (name, value, isProperty = false) => {
            const sel = isProperty
                ? `meta[property="${name}"]`
                : `meta[name="${name}"]`;
            let tag = document.querySelector(sel);
            if (!tag) {
                tag = document.createElement("meta");
                if (isProperty) tag.setAttribute("property", name);
                else tag.setAttribute("name", name);
                document.head.appendChild(tag);
            }
            tag.setAttribute("content", value);
        };
        setMeta("description", desc);
        setMeta("robots", "index, follow");
        setMeta("og:title", title, true);
        setMeta("og:description", desc, true);
        setMeta(
            "og:url",
            `https://fotografsgarcia.com/fotografia-bodas-${city.toLowerCase()}`,
            true,
        );
        // canonical
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute(
            "href",
            `https://fotografsgarcia.com/fotografia-bodas-${city.toLowerCase()}`,
        );
        // Restore on unmount
        return () => {
            document.title =
                "Silvia García · Fotógrafa de Bodas en Bilbao y Margen Izquierda | fotografsgarcia.com";
            canonical.setAttribute("href", "https://fotografsgarcia.com/");
        };
    }, [city, data.metaDescription]);

    const whatsappMsg = `Hola Silvia, vivo en ${city} y me gustaría información sobre tu fotografía de bodas.`;

    return (
        <div className="min-h-screen bg-[#FAFAF8] text-[#1C1A18]">
            <header className="border-b border-[#e6e2db]">
                <div className="max-w-5xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center" data-testid={`loc-${city.toLowerCase()}-logo`}>
                        <Logo size="sm" />
                    </Link>
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#1C1A18]/70 hover:text-[#1C1A18]"
                        data-testid={`loc-${city.toLowerCase()}-back`}
                    >
                        <ArrowLeft size={14} /> Volver
                    </Link>
                </div>
            </header>

            {/* HERO */}
            <section className="relative max-w-5xl mx-auto px-6 lg:px-12 pt-16 pb-12">
                <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[#C4A882]">
                    <MapPin size={12} /> {city}, Bizkaia · País Vasco
                </span>
                <h1
                    className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1C1A18] mt-4 leading-[1.05]"
                    data-testid={`loc-${city.toLowerCase()}-h1`}
                >
                    Fotógrafa de bodas en {city}
                </h1>
                <p className="mt-6 text-lg text-[#3b3833] font-light leading-relaxed max-w-3xl">
                    {data.intro}
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={() => openWhatsApp(whatsappMsg)}
                        data-testid={`loc-${city.toLowerCase()}-cta-whatsapp`}
                        className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#25D366] text-white rounded-full text-[10px] uppercase tracking-[0.28em] hover:bg-[#1ebd5b] active:scale-95 transition-all shadow-lg"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M19.077 4.928A9.94 9.94 0 0012.012 2C6.486 2 1.989 6.494 1.985 12.018a9.94 9.94 0 001.337 4.998L2 22l5.13-1.345a9.97 9.97 0 004.778 1.218h.004c5.526 0 10.022-4.494 10.026-10.018a9.94 9.94 0 00-2.861-7.927z" />
                        </svg>
                        Reservar fecha
                    </button>
                    <Link
                        to="/#contacto"
                        data-testid={`loc-${city.toLowerCase()}-cta-form`}
                        className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#1C1A18] text-[10px] uppercase tracking-[0.28em] hover:bg-[#1C1A18] hover:text-white transition-colors"
                    >
                        Formulario de contacto
                    </Link>
                </div>
            </section>

            {/* BODY */}
            <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-16 grid md:grid-cols-3 gap-10">
                <div className="md:col-span-2 space-y-6 text-[#3b3833] font-light leading-relaxed text-[15px]">
                    {data.paragraphs.map((p, i) => (
                        <p key={i}>{p}</p>
                    ))}

                    <h2 className="font-display text-2xl text-[#1C1A18] pt-4">
                        ¿Qué incluyen mis reportajes de boda en {city}?
                    </h2>
                    <ul className="space-y-3">
                        {data.includes.map((item, i) => (
                            <li key={i} className="flex gap-3">
                                <span className="text-[#C4A882] shrink-0">·</span>
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <aside className="bg-[#1C1A18] text-[#FAFAF8] p-7 rounded-lg space-y-5 h-fit sticky top-24">
                    <span className="text-[10px] tracking-[0.4em] uppercase text-[#C4A882]">
                        Por qué elegirme
                    </span>
                    <div className="flex gap-3">
                        <Heart
                            size={18}
                            strokeWidth={1.4}
                            className="text-[#C4A882] shrink-0 mt-1"
                        />
                        <div>
                            <p className="font-display text-base">
                                Cercanía local
                            </p>
                            <p className="text-xs text-[#FAFAF8]/70 mt-1">
                                Conozco {city} y sus rincones más fotogénicos.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <Camera
                            size={18}
                            strokeWidth={1.4}
                            className="text-[#C4A882] shrink-0 mt-1"
                        />
                        <div>
                            <p className="font-display text-base">
                                Estilo documental
                            </p>
                            <p className="text-xs text-[#FAFAF8]/70 mt-1">
                                Capturo lo que pasa, sin posados forzados.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <Award
                            size={18}
                            strokeWidth={1.4}
                            className="text-[#C4A882] shrink-0 mt-1"
                        />
                        <div>
                            <p className="font-display text-base">
                                Entrega cuidada
                            </p>
                            <p className="text-xs text-[#FAFAF8]/70 mt-1">
                                Álbumes y productos personalizados con calidad
                                premium.
                            </p>
                        </div>
                    </div>
                    <div className="pt-3 border-t border-[#FAFAF8]/15">
                        <button
                            type="button"
                            onClick={() => openWhatsApp(whatsappMsg)}
                            data-testid={`loc-${city.toLowerCase()}-aside-cta`}
                            className="w-full px-5 py-3 bg-[#C4A882] text-[#1C1A18] rounded-full text-[10px] uppercase tracking-[0.28em] hover:bg-[#d8bf94] active:scale-95 transition-all"
                        >
                            Hablamos por WhatsApp
                        </button>
                    </div>
                </aside>
            </section>

            {/* FAQ-ish closing CTA */}
            <section className="bg-[#f4f1ea] py-14">
                <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
                    <h2 className="font-display text-3xl text-[#1C1A18]">
                        ¿Te casas en {city}? Reservemos tu fecha
                    </h2>
                    <p className="mt-4 text-[#3b3833] font-light">
                        Solo acepto un número limitado de bodas al año para
                        cuidar cada detalle. Cuéntame tu historia y veamos si
                        encajamos.
                    </p>
                    <Link
                        to="/#contacto"
                        data-testid={`loc-${city.toLowerCase()}-bottom-cta`}
                        className="mt-7 inline-flex items-center gap-2 px-8 py-3.5 bg-[#1C1A18] text-white rounded-full text-[10px] uppercase tracking-[0.28em] hover:bg-[#C4A882] hover:text-[#1C1A18] transition-colors"
                    >
                        Pedir disponibilidad
                    </Link>
                </div>
            </section>
        </div>
    );
}
